import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, Play, Pause, Square, Coffee, CheckCircle, XCircle, Edit, Trash2, AlertTriangle, Filter, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDate } from '@/utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

// Check if location permission is granted
const checkLocationPermission = async () => {
  // Check if geolocation API is available
  if (!navigator.geolocation) {
    return { supported: false, status: 'unsupported' };
  }
  
  // Check permission status using Permissions API if available
  if (navigator.permissions && navigator.permissions.query) {
    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      return { supported: true, status: result.state }; // 'granted', 'denied', 'prompt'
    } catch (e) {
      // Permissions API not fully supported, will need to try geolocation directly
      return { supported: true, status: 'unknown' };
    }
  }
  
  return { supported: true, status: 'unknown' };
};

// Helper to get device location with proper error handling
const getDeviceLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject({ code: 'UNSUPPORTED', message: 'Geolocation is not supported by this browser' });
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        // Try to get address using reverse geocoding (free service)
        let location_address = null;
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            { headers: { 'User-Agent': 'WilyrashFarmApp/1.0' } }
          );
          const data = await response.json();
          if (data.display_name) {
            // Shorten the address
            const parts = data.display_name.split(',').slice(0, 3);
            location_address = parts.join(', ');
          }
        } catch (e) {
          console.log('Could not get address:', e);
        }
        
        resolve({ latitude, longitude, location_address });
      },
      (error) => {
        // Handle specific error codes
        let errorCode = 'UNKNOWN';
        let errorMessage = 'Failed to get location';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorCode = 'PERMISSION_DENIED';
            errorMessage = 'Location permission was denied. Please enable location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorCode = 'POSITION_UNAVAILABLE';
            errorMessage = 'Location information is unavailable. Please check your device settings.';
            break;
          case error.TIMEOUT:
            errorCode = 'TIMEOUT';
            errorMessage = 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage = error.message || 'Unknown location error';
        }
        
        reject({ code: errorCode, message: errorMessage });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  });
};

// Helper to get local time info
const getLocalTimeInfo = () => {
  const now = new Date();
  return {
    local_time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }),
    device_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
};

const TimeSheet = () => {
  const [clockStatus, setClockStatus] = useState('not_clocked_in');
  const [currentEntry, setCurrentEntry] = useState(null);
  const [myEntries, setMyEntries] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationErrorOpen, setLocationErrorOpen] = useState(false);
  const [locationErrorDetails, setLocationErrorDetails] = useState({ code: '', message: '' });
  const [pendingAction, setPendingAction] = useState(null); // 'clock_in', 'clock_out', 'break_start', 'break_end', 'after_hour'
  const [editOpen, setEditOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({
    clock_in: '',
    clock_out: '',
    status: '',
    admin_notes: ''
  });
  
  // Users exempt from forced location requirement (can clock without location)
  const LOCATION_EXEMPT_USERS = ['Baly', 'Seun10', 'Samy72'];
  
  // After Hour states
  const [afterHourStatus, setAfterHourStatus] = useState('on_premises');
  const [afterHourMovements, setAfterHourMovements] = useState([]);
  const [allAfterHourMovements, setAllAfterHourMovements] = useState([]);
  const [afterHourReason, setAfterHourReason] = useState('');
  const [showAfterHourDialog, setShowAfterHourDialog] = useState(false);
  const [afterHourType, setAfterHourType] = useState('out'); // 'out' or 'in'
  
  // Filters for admin view
  const [filterUser, setFilterUser] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [staffList, setStaffList] = useState([]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const isManager = user.role === 'manager';
  const canManage = isAdmin || isManager;

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchClockStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/timesheet/status`, getAuthHeader());
      setClockStatus(response.data.status);
      setCurrentEntry(response.data.entry);
    } catch (error) {
      console.error('Failed to fetch clock status');
    }
  }, []);

  const fetchMyEntries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/timesheet/my-entries`, getAuthHeader());
      setMyEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch time entries');
    }
  }, []);

  const fetchAllEntries = useCallback(async () => {
    if (!canManage) return;
    try {
      const params = new URLSearchParams();
      if (filterUser) params.append('user_id', filterUser);
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      if (filterStatus) params.append('status', filterStatus);
      
      const response = await axios.get(`${API}/timesheet/all-entries?${params}`, getAuthHeader());
      setAllEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch all entries');
    }
  }, [canManage, filterUser, filterStartDate, filterEndDate, filterStatus]);

  const fetchStaffList = useCallback(async () => {
    if (!canManage) return;
    try {
      const response = await axios.get(`${API}/users`, getAuthHeader());
      const staff = response.data.filter(u => ['staff', 'manager'].includes(u.role));
      setStaffList(staff);
    } catch (error) {
      console.error('Failed to fetch staff list');
    }
  }, [canManage]);

  // After Hour fetch functions
  const fetchAfterHourStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/timesheet/after-hour/status`, getAuthHeader());
      setAfterHourStatus(response.data.status);
    } catch (error) {
      console.error('Failed to fetch after-hour status');
    }
  }, []);

  const fetchAfterHourMovements = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/timesheet/after-hour/my-movements`, getAuthHeader());
      setAfterHourMovements(response.data);
    } catch (error) {
      console.error('Failed to fetch after-hour movements');
    }
  }, []);

  const fetchAllAfterHourMovements = useCallback(async () => {
    if (!canManage) return;
    try {
      const response = await axios.get(`${API}/timesheet/after-hour/all-movements`, getAuthHeader());
      setAllAfterHourMovements(response.data);
    } catch (error) {
      console.error('Failed to fetch all after-hour movements');
    }
  }, [canManage]);

  useEffect(() => {
    fetchClockStatus();
    fetchMyEntries();
    fetchAfterHourStatus();
    fetchAfterHourMovements();
    if (canManage) {
      fetchAllEntries();
      fetchStaffList();
      fetchAllAfterHourMovements();
    }
  }, [fetchClockStatus, fetchMyEntries, fetchAllEntries, fetchStaffList, fetchAfterHourStatus, fetchAfterHourMovements, fetchAllAfterHourMovements, canManage]);

  const handleClockIn = async () => {
    setLoading(true);
    setLocationLoading(true);
    
    // Check if current user is exempt from location requirement
    const isExempt = LOCATION_EXEMPT_USERS.includes(user.username);
    
    try {
      // Get device location and local time
      toast.info('Getting your location...');
      let locationData = { latitude: null, longitude: null, location_address: null };
      
      try {
        locationData = await getDeviceLocation();
      } catch (locationError) {
        // If user is exempt, allow them to proceed without location
        if (isExempt) {
          console.log('Location unavailable for exempt user, proceeding without location');
          toast.info('Location unavailable, clocking in without location');
        } else {
          // Non-exempt users must have location
          throw locationError;
        }
      }
      
      const timeInfo = getLocalTimeInfo();
      
      const payload = {
        ...locationData,
        ...timeInfo
      };
      
      const response = await axios.post(`${API}/timesheet/clock-in`, payload, getAuthHeader());
      toast.success(response.data.message + (locationData.location_address ? ` at ${locationData.location_address}` : ''));
      fetchClockStatus();
      fetchMyEntries();
      if (canManage) fetchAllEntries();
    } catch (error) {
      // Check if it's a location error
      if (error.code && (error.code === 'PERMISSION_DENIED' || error.code === 'POSITION_UNAVAILABLE' || error.code === 'UNSUPPORTED' || error.code === 'TIMEOUT')) {
        setLocationErrorDetails({ code: error.code, message: error.message });
        setPendingAction('clock_in');
        setLocationErrorOpen(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to clock in');
      }
    } finally {
      setLoading(false);
      setLocationLoading(false);
    }
  };

  const handleClockOut = async () => {
    setLoading(true);
    setLocationLoading(true);
    
    // Check if current user is exempt from location requirement
    const isExempt = LOCATION_EXEMPT_USERS.includes(user.username);
    
    try {
      // Get device location and local time
      toast.info('Getting your location...');
      let locationData = { latitude: null, longitude: null, location_address: null };
      
      try {
        locationData = await getDeviceLocation();
      } catch (locationError) {
        // If user is exempt, allow them to proceed without location
        if (isExempt) {
          console.log('Location unavailable for exempt user, proceeding without location');
          toast.info('Location unavailable, clocking out without location');
        } else {
          // Non-exempt users must have location
          throw locationError;
        }
      }
      
      const timeInfo = getLocalTimeInfo();
      
      const payload = {
        ...locationData,
        ...timeInfo
      };
      
      const response = await axios.post(`${API}/timesheet/clock-out`, payload, getAuthHeader());
      toast.success(response.data.message);
      fetchClockStatus();
      fetchMyEntries();
      if (canManage) fetchAllEntries();
    } catch (error) {
      // Check if it's a location error
      if (error.code && (error.code === 'PERMISSION_DENIED' || error.code === 'POSITION_UNAVAILABLE' || error.code === 'UNSUPPORTED' || error.code === 'TIMEOUT')) {
        setLocationErrorDetails({ code: error.code, message: error.message });
        setPendingAction('clock_out');
        setLocationErrorOpen(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to clock out');
      }
    } finally {
      setLoading(false);
      setLocationLoading(false);
    }
  };

  const handleStartBreak = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/timesheet/break/start`, {}, getAuthHeader());
      toast.success(response.data.message);
      fetchClockStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start break');
    } finally {
      setLoading(false);
    }
  };

  const handleEndBreak = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/timesheet/break/end`, {}, getAuthHeader());
      toast.success(response.data.message);
      fetchClockStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end break');
    } finally {
      setLoading(false);
    }
  };

  // After Hour Movement Handlers
  const openAfterHourDialog = (type) => {
    setAfterHourType(type);
    setAfterHourReason('');
    setShowAfterHourDialog(true);
  };

  const handleAfterHourMovement = async () => {
    setLoading(true);
    setLocationLoading(true);
    
    // Check if current user is exempt from location requirement
    const isExempt = LOCATION_EXEMPT_USERS.includes(user.username);
    
    try {
      toast.info('Getting your location...');
      let locationData = { latitude: null, longitude: null, location_address: null };
      
      try {
        locationData = await getDeviceLocation();
      } catch (locationError) {
        // If user is exempt, allow them to proceed without location
        if (isExempt) {
          console.log('Location unavailable for exempt user, proceeding without location');
          toast.info('Location unavailable, recording movement without location');
        } else {
          // Non-exempt users must have location
          throw locationError;
        }
      }
      
      const timeInfo = getLocalTimeInfo();
      
      const payload = {
        movement_type: afterHourType,
        reason: afterHourReason,
        ...locationData,
        ...timeInfo
      };
      
      const response = await axios.post(`${API}/timesheet/after-hour`, payload, getAuthHeader());
      toast.success(response.data.message + (locationData.location_address ? ` at ${locationData.location_address}` : ''));
      setShowAfterHourDialog(false);
      setAfterHourReason('');
      fetchAfterHourStatus();
      fetchAfterHourMovements();
      if (canManage) fetchAllAfterHourMovements();
    } catch (error) {
      // Check if it's a location error
      if (error.code && (error.code === 'PERMISSION_DENIED' || error.code === 'POSITION_UNAVAILABLE' || error.code === 'UNSUPPORTED' || error.code === 'TIMEOUT')) {
        setLocationErrorDetails({ code: error.code, message: error.message });
        setPendingAction('after_hour');
        setLocationErrorOpen(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to record movement');
      }
    } finally {
      setLoading(false);
      setLocationLoading(false);
    }
  };

  const handleDeleteAfterHourMovement = async (movementId) => {
    if (!window.confirm('Are you sure you want to delete this movement record?')) return;
    try {
      await axios.delete(`${API}/timesheet/after-hour/${movementId}`, getAuthHeader());
      toast.success('Movement record deleted');
      fetchAfterHourMovements();
      if (canManage) fetchAllAfterHourMovements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete record');
    }
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setEditForm({
      clock_in: entry.clock_in ? new Date(entry.clock_in).toISOString().slice(0, 16) : '',
      clock_out: entry.clock_out ? new Date(entry.clock_out).toISOString().slice(0, 16) : '',
      status: entry.status || 'pending',
      admin_notes: entry.admin_notes || ''
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        clock_in: editForm.clock_in ? new Date(editForm.clock_in).toISOString() : null,
        clock_out: editForm.clock_out ? new Date(editForm.clock_out).toISOString() : null,
        status: editForm.status,
        admin_notes: editForm.admin_notes
      };
      await axios.put(`${API}/timesheet/${editingEntry.id}`, payload, getAuthHeader());
      toast.success('Time entry updated');
      setEditOpen(false);
      fetchAllEntries();
      fetchMyEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update entry');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await axios.delete(`${API}/timesheet/${entryId}`, getAuthHeader());
      toast.success('Entry deleted');
      fetchAllEntries();
      fetchMyEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const handleApprove = async (entry) => {
    try {
      await axios.put(`${API}/timesheet/${entry.id}`, { status: 'approved' }, getAuthHeader());
      toast.success(`Approved entry for ${entry.user_name}`);
      fetchAllEntries();
    } catch (error) {
      toast.error('Failed to approve entry');
    }
  };

  const handleReject = async (entry) => {
    try {
      await axios.put(`${API}/timesheet/${entry.id}`, { status: 'rejected' }, getAuthHeader());
      toast.success(`Rejected entry for ${entry.user_name}`);
      fetchAllEntries();
    } catch (error) {
      toast.error('Failed to reject entry');
    }
  };

  // Retry the pending action after user enables location
  const handleRetryWithLocation = async () => {
    setLocationErrorOpen(false);
    
    // Small delay to allow user to enable location
    await new Promise(resolve => setTimeout(resolve, 500));
    
    switch (pendingAction) {
      case 'clock_in':
        handleClockIn();
        break;
      case 'clock_out':
        handleClockOut();
        break;
      case 'after_hour':
        handleAfterHourMovement();
        break;
      default:
        break;
    }
    setPendingAction(null);
  };

  // Get device-specific instructions for enabling location
  const getLocationInstructions = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isChrome = /chrome/.test(userAgent) && !/edge/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);
    const isFirefox = /firefox/.test(userAgent);
    
    if (isIOS) {
      return {
        device: 'iPhone/iPad',
        steps: [
          'Open Settings app on your device',
          'Scroll down and tap on Safari (or your browser)',
          'Tap on "Location" under "Allow Safari to Access"',
          'Select "Ask" or "Allow"',
          'Return to this app and tap "Try Again"'
        ],
        settingsNote: 'You may also need to enable Location Services: Settings > Privacy & Security > Location Services > ON'
      };
    } else if (isAndroid) {
      return {
        device: 'Android',
        steps: [
          'Tap the lock icon in your browser address bar',
          'Tap "Site settings" or "Permissions"',
          'Find "Location" and change it to "Allow"',
          'Alternatively: Go to Settings > Apps > Chrome > Permissions > Location',
          'Return to this app and tap "Try Again"'
        ],
        settingsNote: 'Also ensure GPS/Location is enabled: Pull down notification shade > Tap Location icon to enable'
      };
    } else if (isChrome) {
      return {
        device: 'Chrome Browser',
        steps: [
          'Click the lock/info icon in the address bar (left of URL)',
          'Click "Site settings"',
          'Find "Location" and change to "Allow"',
          'Refresh the page and try again'
        ],
        settingsNote: 'You can also go to chrome://settings/content/location to manage all location permissions'
      };
    } else if (isSafari) {
      return {
        device: 'Safari Browser',
        steps: [
          'Click Safari in the menu bar',
          'Click "Settings for This Website" (or Preferences > Websites > Location)',
          'Set Location to "Allow"',
          'Refresh the page and try again'
        ],
        settingsNote: 'On Mac: System Preferences > Security & Privacy > Privacy > Location Services > Enable for Safari'
      };
    } else if (isFirefox) {
      return {
        device: 'Firefox Browser',
        steps: [
          'Click the lock icon in the address bar',
          'Click "Connection secure" or site info',
          'Click "More Information"',
          'Go to Permissions tab',
          'Find Location and uncheck "Use Default" then select "Allow"'
        ],
        settingsNote: null
      };
    } else {
      return {
        device: 'Your Browser',
        steps: [
          'Look for a location icon in your address bar and click it',
          'Or go to your browser settings',
          'Find Privacy/Permissions/Site Settings',
          'Enable location access for this website',
          'Refresh the page and try again'
        ],
        settingsNote: null
      };
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const getStatusBadge = (status, entry = null) => {
    // Check if auto-clocked out
    if (entry?.auto_clocked_out) {
      return (
        <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium" title={entry.auto_clock_out_reason}>
          Auto Clocked Out
        </span>
      );
    }
    
    switch (status) {
      case 'approved':
        return <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-medium">Approved</span>;
      case 'rejected':
        return <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-medium">Rejected</span>;
      default:
        return <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">Pending</span>;
    }
  };

  return (
    <div className="space-y-6" data-testid="timesheet-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Time Sheet
          </h1>
          <p className="text-lg text-slate-600 mt-2">Track your work hours and breaks</p>
        </div>
      </div>

      {/* Clock Display & Controls */}
      <div className="card p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          {/* Current Time Display */}
          <div className="text-center lg:text-left">
            <p className="text-sm text-slate-500 mb-1">Current Time</p>
            <div className="text-5xl font-bold text-slate-900 font-mono" data-testid="current-time">
              {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
            </div>
            <p className="text-slate-600 mt-1">{formatDate(currentTime.toISOString().split('T')[0])}</p>
          </div>

          {/* Status & Controls */}
          <div className="flex flex-col items-center gap-4">
            {/* Status Badge */}
            <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
              clockStatus === 'clocked_in' ? 'bg-emerald-100 text-emerald-700' :
              clockStatus === 'on_break' ? 'bg-amber-100 text-amber-700' :
              clockStatus === 'clocked_out' ? 'bg-blue-100 text-blue-700' :
              'bg-slate-100 text-slate-600'
            }`} data-testid="clock-status">
              {clockStatus === 'clocked_in' && 'Currently Working'}
              {clockStatus === 'on_break' && 'On Break'}
              {clockStatus === 'clocked_out' && 'Clocked Out for Today'}
              {clockStatus === 'not_clocked_in' && 'Not Clocked In'}
            </div>

            {/* Clock In/Out Times */}
            {currentEntry && (
              <div className="flex flex-col items-center gap-2">
                <div className="flex gap-6 text-sm">
                  <div>
                    <span className="text-slate-500">In:</span>
                    <span className="ml-2 font-medium">{currentEntry.clock_in_local_time || formatTime(currentEntry.clock_in)}</span>
                  </div>
                  {currentEntry.clock_out && (
                    <div>
                      <span className="text-slate-500">Out:</span>
                      <span className="ml-2 font-medium">{currentEntry.clock_out_local_time || formatTime(currentEntry.clock_out)}</span>
                    </div>
                  )}
                  {currentEntry.total_hours && (
                    <div>
                      <span className="text-slate-500">Total:</span>
                      <span className={`ml-2 font-medium ${currentEntry.is_overtime ? 'text-amber-600' : ''}`}>
                        {currentEntry.total_hours}h
                        {currentEntry.is_overtime && <AlertTriangle className="inline w-4 h-4 ml-1" />}
                      </span>
                    </div>
                  )}
                </div>
                {/* Location Info */}
                {currentEntry.clock_in_location && (
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    <span>{currentEntry.clock_in_location}</span>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              {clockStatus === 'not_clocked_in' && (
                <Button 
                  onClick={handleClockIn} 
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="clock-in-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Clock In
                </Button>
              )}
              
              {clockStatus === 'clocked_in' && (
                <>
                  <Button 
                    onClick={handleStartBreak} 
                    disabled={loading}
                    variant="outline"
                    className="border-amber-600 text-amber-600 hover:bg-amber-50"
                    data-testid="start-break-btn"
                  >
                    <Coffee className="w-4 h-4 mr-2" />
                    Start Break
                  </Button>
                  <Button 
                    onClick={handleClockOut} 
                    disabled={loading}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="clock-out-btn"
                  >
                    <Square className="w-4 h-4 mr-2" />
                    Clock Out
                  </Button>
                </>
              )}
              
              {clockStatus === 'on_break' && (
                <Button 
                  onClick={handleEndBreak} 
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="end-break-btn"
                >
                  <Pause className="w-4 h-4 mr-2" />
                  End Break
                </Button>
              )}
              
              {clockStatus === 'clocked_out' && (
                <div className="text-slate-500 text-sm">
                  <CheckCircle className="inline w-4 h-4 mr-1 text-emerald-500" />
                  You&apos;ve completed your shift for today
                </div>
              )}
            </div>
          </div>

          {/* IP Address Info */}
          {currentEntry && (
            <div className="text-xs text-slate-400 text-center lg:text-right">
              {currentEntry.clock_in_ip && <div>Clock In IP: {currentEntry.clock_in_ip}</div>}
              {currentEntry.clock_out_ip && <div>Clock Out IP: {currentEntry.clock_out_ip}</div>}
            </div>
          )}
        </div>
      </div>

      {/* After Hour Movement Section */}
      <div className="card p-6 border-l-4 border-purple-500">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">After Hour Movement</h3>
            <p className="text-sm text-slate-600">Track movements outside of regular work hours for safety monitoring</p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Status Badge */}
            <div className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              afterHourStatus === 'on_premises' 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'bg-orange-100 text-orange-700'
            }`} data-testid="after-hour-status">
              {afterHourStatus === 'on_premises' ? 'On Premises' : 'Off Premises'}
            </div>
            
            {/* After Hour Buttons */}
            <div className="flex gap-2">
              {(clockStatus === 'clocked_out' || clockStatus === 'not_clocked_in') && (
                <>
                  {afterHourStatus === 'on_premises' ? (
                    <Button 
                      onClick={() => openAfterHourDialog('out')}
                      disabled={loading}
                      variant="outline"
                      className="border-orange-500 text-orange-600 hover:bg-orange-50"
                      data-testid="after-hour-out-btn"
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Leaving Premises
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => openAfterHourDialog('in')}
                      disabled={loading}
                      variant="outline"
                      className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                      data-testid="after-hour-in-btn"
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Returning to Premises
                    </Button>
                  )}
                </>
              )}
              {(clockStatus === 'clocked_in' || clockStatus === 'on_break') && (
                <div className="text-sm text-slate-500 italic">
                  Available after clocking out
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* My Time Entries */}
      <div className="card">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">My Time Entries</h2>
        </div>
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Clock In</th>
                <th>Clock Out</th>
                <th>Location</th>
                <th>Breaks</th>
                <th>Total Hours</th>
                <th>Overtime</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody data-testid="my-entries-table">
              {myEntries.length === 0 ? (
                <tr><td colSpan="8" className="text-center text-slate-500 py-8">No time entries yet</td></tr>
              ) : (
                myEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td className="font-medium">{formatDate(entry.date)}</td>
                    <td>{entry.clock_in_local_time || formatTime(entry.clock_in)}</td>
                    <td>{entry.clock_out_local_time || formatTime(entry.clock_out)}</td>
                    <td className="text-xs max-w-[120px]">
                      {entry.clock_in_location ? (
                        <div className="flex items-start gap-1">
                          <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-emerald-600" />
                          <span className="truncate" title={entry.clock_in_location}>{entry.clock_in_location}</span>
                        </div>
                      ) : '-'}
                    </td>
                    <td>{entry.total_break_minutes ? `${Math.round(entry.total_break_minutes)}m` : '-'}</td>
                    <td className={entry.is_overtime ? 'text-amber-600 font-semibold' : ''}>
                      {entry.total_hours ? `${entry.total_hours}h` : '-'}
                    </td>
                    <td>
                      {entry.is_overtime ? (
                        <span className="text-amber-600 flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          {parseFloat(entry.overtime_hours).toFixed(2)}h
                        </span>
                      ) : '-'}
                    </td>
                    <td>{getStatusBadge(entry.status, entry)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Admin View - All Entries */}
      {canManage && (
        <div className="card">
          <div className="p-4 border-b">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h2 className="text-lg font-semibold">All Staff Time Entries</h2>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-2 items-center">
                <Select value={filterUser || 'all'} onValueChange={(v) => setFilterUser(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Staff" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff</SelectItem>
                    {staffList.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input 
                  type="date" 
                  value={filterStartDate} 
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="w-36"
                  placeholder="Start Date"
                />
                <Input 
                  type="date" 
                  value={filterEndDate} 
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="w-36"
                  placeholder="End Date"
                />
                <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={fetchAllEntries} size="sm">
                  <Filter className="w-4 h-4 mr-1" />
                  Apply
                </Button>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Date</th>
                  <th>Clock In</th>
                  <th>Clock Out</th>
                  <th>Location</th>
                  <th>Breaks</th>
                  <th>Total</th>
                  <th>OT</th>
                  <th>IP (In/Out)</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody data-testid="all-entries-table">
                {allEntries.length === 0 ? (
                  <tr><td colSpan="11" className="text-center text-slate-500 py-8">No entries found</td></tr>
                ) : (
                  allEntries.map((entry) => (
                    <tr key={entry.id} className={entry.is_overtime ? 'bg-amber-50' : ''}>
                      <td className="font-medium">{entry.user_name}</td>
                      <td>{formatDate(entry.date)}</td>
                      <td>
                        <div>{entry.clock_in_local_time || formatTime(entry.clock_in)}</div>
                      </td>
                      <td>
                        <div>{entry.clock_out_local_time || formatTime(entry.clock_out)}</div>
                      </td>
                      <td className="text-xs max-w-[150px]">
                        {entry.clock_in_location ? (
                          <div className="flex items-start gap-1">
                            <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-emerald-600" />
                            <span className="truncate" title={entry.clock_in_location}>{entry.clock_in_location}</span>
                          </div>
                        ) : entry.clock_in_coords ? (
                          <div className="text-slate-400 text-xs">
                            {entry.clock_in_coords.lat?.toFixed(4)}, {entry.clock_in_coords.lng?.toFixed(4)}
                          </div>
                        ) : '-'}
                      </td>
                      <td>{entry.total_break_minutes ? `${Math.round(entry.total_break_minutes)}m` : '-'}</td>
                      <td className={entry.is_overtime ? 'text-amber-600 font-semibold' : ''}>
                        {entry.total_hours ? `${entry.total_hours}h` : '-'}
                      </td>
                      <td>
                        {entry.is_overtime ? (
                          <span className="text-amber-600 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {parseFloat(entry.overtime_hours).toFixed(2)}h
                          </span>
                        ) : '-'}
                      </td>
                      <td className="text-xs text-slate-500">
                        {entry.clock_in_ip?.slice(0, 15) || '-'}
                        {entry.clock_out_ip && ` / ${entry.clock_out_ip.slice(0, 15)}`}
                      </td>
                      <td>{getStatusBadge(entry.status, entry)}</td>
                      <td>
                        <div className="flex gap-1">
                          {entry.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(entry)}
                                className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"
                                title="Approve"
                                data-testid={`approve-${entry.id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleReject(entry)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                title="Reject"
                                data-testid={`reject-${entry.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {isAdmin && (
                            <>
                              <button
                                onClick={() => openEditDialog(entry)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                title="Edit"
                                data-testid={`edit-${entry.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(entry.id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                title="Delete"
                                data-testid={`delete-${entry.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Admin View - All After Hour Movements */}
      {canManage && (
        <div className="card">
          <div className="p-4 border-b border-l-4 border-purple-500">
            <h2 className="text-lg font-semibold">All Staff After Hour Movements</h2>
            <p className="text-sm text-slate-500">Track staff movements outside of work hours for safety</p>
          </div>
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Movement</th>
                  <th>Location</th>
                  <th>Reason</th>
                  <th>IP Address</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody data-testid="after-hour-movements-table">
                {allAfterHourMovements.length === 0 ? (
                  <tr><td colSpan={isAdmin ? "8" : "7"} className="text-center text-slate-500 py-8">No after-hour movements recorded</td></tr>
                ) : (
                  allAfterHourMovements.map((movement) => (
                    <tr key={movement.id}>
                      <td className="font-medium">{movement.user_name}</td>
                      <td>{formatDate(movement.date)}</td>
                      <td>{movement.local_time || formatTime(movement.timestamp)}</td>
                      <td>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          movement.movement_type === 'out' 
                            ? 'bg-orange-100 text-orange-700' 
                            : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {movement.movement_type === 'out' ? 'Left Premises' : 'Returned'}
                        </span>
                      </td>
                      <td className="text-xs max-w-[150px]">
                        {movement.location ? (
                          <div className="flex items-start gap-1">
                            <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-purple-600" />
                            <span className="truncate" title={movement.location}>{movement.location}</span>
                          </div>
                        ) : movement.coords ? (
                          <div className="text-slate-400 text-xs">
                            {movement.coords.lat?.toFixed(4)}, {movement.coords.lng?.toFixed(4)}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="text-sm max-w-[150px] truncate" title={movement.reason}>
                        {movement.reason || '-'}
                      </td>
                      <td className="text-xs text-slate-500">{movement.ip_address || '-'}</td>
                      {isAdmin && (
                        <td>
                          <button
                            onClick={() => handleDeleteAfterHourMovement(movement.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Time Entry - {editingEntry?.user_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Clock In</Label>
                <Input
                  type="datetime-local"
                  value={editForm.clock_in}
                  onChange={(e) => setEditForm({ ...editForm, clock_in: e.target.value })}
                />
              </div>
              <div>
                <Label>Clock Out</Label>
                <Input
                  type="datetime-local"
                  value={editForm.clock_out}
                  onChange={(e) => setEditForm({ ...editForm, clock_out: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Admin Notes</Label>
              <Textarea
                value={editForm.admin_notes}
                onChange={(e) => setEditForm({ ...editForm, admin_notes: e.target.value })}
                placeholder="Add notes about this entry..."
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1">Save Changes</Button>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* After Hour Movement Dialog */}
      <Dialog open={showAfterHourDialog} onOpenChange={setShowAfterHourDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {afterHourType === 'out' ? 'Leaving Premises' : 'Returning to Premises'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-slate-50">
              <p className="text-sm text-slate-600">
                {afterHourType === 'out' 
                  ? 'You are about to record leaving the premises outside of work hours. Your location will be captured for safety purposes.'
                  : 'You are about to record returning to the premises. Your location will be captured for safety purposes.'}
              </p>
            </div>
            <div>
              <Label>Reason (Optional)</Label>
              <Textarea
                value={afterHourReason}
                onChange={(e) => setAfterHourReason(e.target.value)}
                placeholder={afterHourType === 'out' ? 'e.g., Personal errand, Medical appointment...' : 'e.g., Returned from errand...'}
                data-testid="after-hour-reason"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleAfterHourMovement}
                disabled={loading}
                className={afterHourType === 'out' ? 'flex-1 bg-orange-600 hover:bg-orange-700' : 'flex-1 bg-emerald-600 hover:bg-emerald-700'}
                data-testid="confirm-after-hour-btn"
              >
                {loading ? 'Getting location...' : (afterHourType === 'out' ? 'Confirm Leaving' : 'Confirm Returning')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowAfterHourDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Location Error/Permission Dialog */}
      <Dialog open={locationErrorOpen} onOpenChange={setLocationErrorOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="location-error-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <MapPin className="w-5 h-5" />
              Location Access Required
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Error Message */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700 font-medium">{locationErrorDetails.message}</p>
              <p className="text-red-600 text-sm mt-1">
                Location is required for clocking in/out to ensure accurate time tracking.
              </p>
            </div>

            {/* Instructions */}
            {(() => {
              const instructions = getLocationInstructions();
              return (
                <div className="space-y-3">
                  <h3 className="font-semibold text-slate-900">
                    How to enable location on {instructions.device}:
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-slate-700">
                    {instructions.steps.map((step, index) => (
                      <li key={index} className="pl-1">{step}</li>
                    ))}
                  </ol>
                  {instructions.settingsNote && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                      <p className="text-amber-800 text-sm">
                        <strong>Note:</strong> {instructions.settingsNote}
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Visual Guide */}
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-3">
                <MapPin className="w-8 h-8 text-emerald-600" />
              </div>
              <p className="text-sm text-slate-600">
                After enabling location, click <strong>"Try Again"</strong> below
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button 
                onClick={handleRetryWithLocation}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="retry-location-btn"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setLocationErrorOpen(false);
                  setPendingAction(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeSheet;
